// Attaches an Entra ID access token to every axios request.
//
// All pages call the global `axios` instance directly, so registering a single
// request interceptor here authenticates every API call without touching the
// pages. The token is fetched silently from the MSAL cache; if interaction is
// required (expired session, consent, etc.) we fall back to a redirect.
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, loginRequest, AUTH_ENABLED } from "./authConfig";
import { notify } from "./notify";
import { VIEW_AS_ID_KEY, VIEW_AS_WRITE_KEY } from "./ViewAsContext";

// Turn an axios error into a human-readable message for the toast.
function errorMessage(error) {
  // No response = the request never reached the server (DNS, CORS, offline,
  // wrong API base URL). This is the failure mode that used to be silent.
  if (!error.response) {
    return "Can't reach the server. Check your connection or try again shortly.";
  }
  const { status, data } = error.response;
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  // FastAPI validation errors arrive as a list of { msg, loc, ... }.
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg).filter(Boolean).join("; ") || `Request failed (${status}).`;
  }
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  return `Request failed (${status}).`;
}

// Surfaces every failed API call as a toast instead of failing silently.
// Registered unconditionally (errors happen with auth on or off). The promise
// is still rejected so existing per-call logic continues to short-circuit.
export function registerErrorInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Best-effort calls (e.g. the location-field conversions) set
      // suppressErrorToast to report their own outcome instead of double-toasting.
      if (!error.config?.suppressErrorToast) notify(errorMessage(error), "error");
      return Promise.reject(error);
    }
  );
}

// Adds the "View as" impersonation headers to every request when a Super Admin
// has an active session (read from localStorage so this works independently of
// React and covers auth-off local dev too). The backend ignores these unless the
// caller is actually a Super Admin, so they can never escalate privileges.
export function registerViewAsInterceptor() {
  axios.interceptors.request.use((cfg) => {
    const id = localStorage.getItem(VIEW_AS_ID_KEY);
    if (id) {
      cfg.headers = cfg.headers || {};
      cfg.headers["X-View-As"] = id;
      if (localStorage.getItem(VIEW_AS_WRITE_KEY) === "true") {
        cfg.headers["X-View-As-Write"] = "true";
      }
    }
    return cfg;
  });
}

export function registerAuthInterceptor() {
  if (!AUTH_ENABLED) return;

  axios.interceptors.request.use(async (cfg) => {
    const account =
      msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) return cfg; // not signed in yet; the AuthGate will redirect

    try {
      const result = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${result.accessToken}`;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Cannot refresh silently -> send the user through interactive login.
        await msalInstance.acquireTokenRedirect(loginRequest);
      } else {
        throw err;
      }
    }
    return cfg;
  });
}
